const WIDGET_BASE_URL = 'https://pms.placidament.com';
export const WIDGET_STYLE_ID = 'placid-widget-search-style';
const WIDGET_INJECTED_SCRIPT_ATTR = 'data-placid-widget-injected';

const PMS_SCRIPT_PATTERNS = [
  'widgetSearch/widgetSearch.js',
  'widgetSearch/scriptPersones.js',
  'flatpickr',
];

let activeGeneration = 0;

const isGenerationActive = (generation: number) => generation === activeGeneration;

export const cleanupPlacidWidget = () => {
  activeGeneration += 1;

  const daterange = document.getElementById('daterange') as (HTMLElement & {
    _flatpickr?: { destroy: () => void };
  }) | null;
  daterange?._flatpickr?.destroy();

  document.querySelectorAll('.flatpickr-calendar').forEach((element) => element.remove());
  document.querySelectorAll('link[href*="flatpickr"]').forEach((element) => element.remove());

  document.querySelectorAll('script[src]').forEach((script) => {
    const src = script.getAttribute('src') || '';
    if (PMS_SCRIPT_PATTERNS.some((pattern) => src.includes(pattern))) {
      script.remove();
    }
  });

  document.querySelectorAll(`script[${WIDGET_INJECTED_SCRIPT_ATTR}="true"]`).forEach((element) => {
    element.remove();
  });

  document.getElementById('widgetSearch')?.replaceChildren();
  document.getElementById(WIDGET_STYLE_ID)?.remove();
};

const ensurePlacidWidgetStyles = () => {
  if (document.getElementById(WIDGET_STYLE_ID)) {
    return;
  }

  const style = document.createElement('style');
  style.id = WIDGET_STYLE_ID;
  style.textContent = `
    .boto-cerca {
      width: 60px;
      padding-left: 20px;
    }
    @media (max-width: 639px) {
      #widgetSearch {
        min-height: 600px;
        overflow: visible;
      }
      .banner-slide,
      .banner-slide .slick-list,
      .banner-slide .slick-track,
      .banner-slide .slick-slide {
        overflow: visible !important;
      }
      .boto-cerca svg {
        margin-top: -20px;
      }
    }
  `;
  document.head.appendChild(style);
};

const ejecutarScripts = (container: HTMLElement, generation: number) => {
  container.querySelectorAll('script').forEach((script) => {
    if (!isGenerationActive(generation)) {
      return;
    }

    if (!script.src) {
      return;
    }

    const newScript = document.createElement('script');
    newScript.src = script.src;
    newScript.async = false;
    newScript.setAttribute(WIDGET_INJECTED_SCRIPT_ATTR, 'true');
    document.body.appendChild(newScript);
  });
};

const inicializarFlatpickr = (lang?: string) => {
  const flatpickr = (window as typeof window & { flatpickr?: (selector: string, options: Record<string, unknown>) => void }).flatpickr;
  if (!flatpickr || !document.getElementById('daterange')) {
    return;
  }

  let flatpickrLang = lang === 'ca' ? 'cat' : lang;
  const supportedLangs = ['es', 'en', 'fr', 'it', 'pt', 'ru', 'de', 'sv', 'cat', 'cs'];

  if (!flatpickrLang || !supportedLangs.includes(flatpickrLang)) {
    flatpickrLang = 'en';
  }

  flatpickr('#daterange', {
    mode: 'range',
    dateFormat: 'd/m/Y',
    altFormat: 'd/m/Y',
    disableMobile: 'true',
    locale: flatpickrLang,
    minDate: 'today',
    onChange: (_selectedDates: Date[], dateStr: string, instance: { element: { value: string } }) => {
      instance.element.value = dateStr.replace('to', '-');
    },
  });
};

const bindSearchButton = (baseUrl: string, lang?: string) => {
  const searchButton = document.getElementById('widgetSearch_searchButton');
  if (!searchButton) {
    return;
  }

  searchButton.addEventListener('click', () => {
    const destination = (document.getElementById('widgetSearch_Destino') as HTMLSelectElement | null)?.value;
    const dates = (document.getElementById('daterange') as HTMLInputElement | null)?.value;
    const adults = (document.getElementById('adultos') as HTMLInputElement | null)?.value;
    const children = (document.getElementById('ninos') as HTMLInputElement | null)?.value;
    const babies = (document.getElementById('bebes') as HTMLInputElement | null)?.value;

    let url = `${baseUrl}/buscar2?destino=${destination}&daterange=${dates}&adultos=${adults}&ninos=${children}&bebes=${babies}`;

    if (lang && lang !== 'es') {
      url = `${baseUrl}/${lang}/buscar2?destino=${destination}&daterange=${dates}&adultos=${adults}&ninos=${children}&bebes=${babies}`;
    }

    window.location.href = url;
  });
};

const renderWidget = async (generation: number) => {
  const widgetContainer = document.getElementById('widgetSearch') as HTMLElement | null;
  if (!widgetContainer || !isGenerationActive(generation)) {
    return;
  }

  let templateUrl = `${WIDGET_BASE_URL}/js/widgetSearch/${widgetContainer.dataset.widgetid}`;
  templateUrl += widgetContainer.dataset.web ? `/${widgetContainer.dataset.web}` : '/1';

  if (widgetContainer.dataset.lang) {
    templateUrl += `/${widgetContainer.dataset.lang}`;
  }

  const response = await fetch(templateUrl);
  if (!response.ok || !isGenerationActive(generation)) {
    return;
  }

  const template = await response.text();
  if (!isGenerationActive(generation) || !document.getElementById('widgetSearch')) {
    return;
  }

  widgetContainer.replaceChildren();

  const templateElement = document.createElement('template');
  templateElement.innerHTML = template;
  const clone = document.importNode(templateElement.content, true);
  widgetContainer.appendChild(clone);

  ejecutarScripts(widgetContainer, generation);

  window.setTimeout(() => {
    if (!isGenerationActive(generation)) {
      return;
    }

    inicializarFlatpickr(widgetContainer.dataset.lang);
    bindSearchButton(WIDGET_BASE_URL, widgetContainer.dataset.lang);
  }, 500);
};

export const mountPlacidWidget = () => {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  cleanupPlacidWidget();
  ensurePlacidWidgetStyles();

  const generation = activeGeneration;
  void renderWidget(generation);

  return () => {
    cleanupPlacidWidget();
  };
};
